import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { FaUser, FaEnvelope, FaLock, FaGoogle, FaShieldAlt } from 'react-icons/fa';
import toast from 'react-hot-toast';

// Terms of Use and Privacy Policy Modal component
const TermsModal = ({ isOpen, onClose, content, title }) => {
  const modalContentRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (modalContentRef.current && !modalContentRef.current.contains(event.target)) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 bg-black/70 flex items-center justify-center p-4 z-50"
    >
      <div 
        ref={modalContentRef}
        className="relative bg-[#1E0B38] border border-purple-500/30 rounded-xl max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col"
      >
        <div className="sticky top-0 bg-[#301555] px-6 py-4 flex justify-between items-center border-b border-purple-500/20">
          <h2 className="text-xl font-bold text-white">{title}</h2>
          <button 
            onClick={onClose}
            aria-label="Close"
            className="text-white hover:text-purple-300 transition-colors p-2 rounded-full bg-purple-600/50 hover:bg-purple-600/70 focus:outline-none focus:ring-2 focus:ring-purple-500/50"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 sm:h-6 sm:w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        <div className="overflow-y-auto p-6 flex-grow text-gray-300">
          <div className="prose prose-invert prose-sm max-w-none">
            {content}
          </div>
        </div>
        <div className="sticky bottom-0 bg-[#301555] px-6 py-4 border-t border-purple-500/20">
          <button 
            onClick={onClose}
            className="w-full py-2 px-4 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white font-medium rounded-lg transition-all"
          >
            I Understand
          </button>
        </div>
      </div>
    </div>
  );
};

export const SignUpForm = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    verificationCode: '',
    acceptTerms: false
  });
  const [formStep, setFormStep] = useState(1); // 1 = registration form, 2 = verification form
  const [isLoading, setIsLoading] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [errors, setErrors] = useState({});
  const [modalOpen, setModalOpen] = useState(false);
  const [modalContent, setModalContent] = useState({
    title: "",
    content: null
  });
  const navigate = useNavigate();
  const location = useLocation();
  const { signup, verifyEmail, resendVerification, pendingVerification, clearPendingVerification } = useAuth();
  
  // Check if user came from AI search
  const searchParams = new URLSearchParams(location.search);
  const isFromAiSearch = searchParams.get('feature') === 'ai-search';
  const returnTo = searchParams.get('returnTo');

  // If there's a pending verification, show the verification form
  useEffect(() => {
    if (pendingVerification) {
      setFormData(prev => ({ ...prev, email: pendingVerification }));
      setFormStep(2);
    }
  }, [pendingVerification]);

  const validateBasicForm = () => {
    const newErrors = {};
    
    if (!formData.name || formData.name.length < 2) {
      newErrors.name = 'Name must be at least 2 characters';
    }
    
    if (!formData.email || !/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    
    if (!formData.password || formData.password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters';
    }
    
    if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords don\'t match';
    }
    
    if (!formData.acceptTerms) {
      newErrors.acceptTerms = 'You must accept the Terms of Use and Privacy Policy';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateVerificationCode = () => {
    const newErrors = {};
    
    if (!formData.verificationCode || formData.verificationCode.length !== 6) {
      newErrors.verificationCode = 'Please enter the 6-digit verification code';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    
    // Clear errors for this field
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = {...prev};
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateBasicForm()) return;
    
    setIsLoading(true);
    try {
      const response = await signup(formData.name, formData.email, formData.password);
      
      if (response.requires_verification) {
        setFormStep(2);
        toast.success(
          'Please check your email for the verification code',
          {
            duration: 5000,
            style: {
              borderRadius: '10px',
              background: '#333',
              color: '#fff',
            },
          }
        );
      }
    } catch (error) {
      console.error('Signup error:', error);
      
      const errorMessage = error.response?.data?.error || 'Something went wrong. Please try again.';
      setErrors({
        form: errorMessage
      });
      
      // Handle disposable email error specially
      if (errorMessage.includes('Disposable email')) {
        setErrors({
          email: errorMessage,
          form: 'Please use a permanent email address'
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    
    if (!validateVerificationCode()) return;
    
    setIsVerifying(true);
    try {
      await verifyEmail(formData.email, formData.verificationCode);
      
      const successMessage = isFromAiSearch 
        ? 'Email verified! Welcome to Zahoot - you now have 3 free AI searches!'
        : 'Email verified successfully! You are now logged in.';
      
      toast.success(
        successMessage,
        {
          duration: 5000,
          style: {
            borderRadius: '10px',
            background: '#333',
            color: '#fff',
          },
        }
      );
      
      // Redirect to return URL or home page after verification
      if (returnTo) {
        navigate(decodeURIComponent(returnTo));
      } else {
        navigate('/');
      }
    } catch (error) {
      console.error('Verification error:', error);
      
      if (error.response?.status === 410) {
        setErrors({
          verificationCode: 'Verification code has expired. Please request a new one.'
        });
      } else {
        setErrors({
          verificationCode: error.response?.data?.error || 'Invalid verification code. Please try again.'
        });
      }
    } finally {
      setIsVerifying(false);
    }
  };

  const handleResendCode = async () => {
    try {
      await resendVerification(formData.email);
      toast.success('Verification code sent successfully!');
    } catch (error) {
      console.error('Error resending code:', error);
      toast.error(error.response?.data?.error || 'Failed to resend verification code');
    }
  };

  const handleBackToSignup = () => {
    setFormStep(1);
    setFormData(prev => ({ ...prev, verificationCode: '' }));
    setErrors({});
    clearPendingVerification();
  };

  const handleGoogleSignIn = () => {
    window.location.href = `${process.env.REACT_APP_API_URL}/api/auth/google`;
  };

  const showTerms = () => {
    setModalContent({
      title: "Terms of Use",
      content: (
        <>
          <h1 className="text-xl font-bold mb-4">Terms of Use</h1>
          <p className="mb-2 text-gray-400">Effective Date: 6th February 2025</p>
          <p className="mb-3 text-gray-400">Last Updated: 6th February 2025</p>
          <p className="mb-3 text-gray-400">Company: Zahoot</p>
          <p className="mb-3 text-gray-400">Website: zahoot.ai</p>
          <p className="mb-6 text-gray-400">Business Address: Louisa Ryland House, 44 Newhall Street, B3 3PL</p>
          
          <h2 className="text-lg font-semibold mt-6 mb-3">1. Introduction</h2>
          <p className="mb-4">Welcome to Zahoot. These Terms of Use govern your access to and use of our website (zahoot.ai) and services. By using our platform, you agree to comply with these terms. If you do not agree, please do not use Zahoot.</p>
          
          <h2 className="text-lg font-semibold mt-6 mb-3">2. Definitions</h2>
          <ul className="list-disc pl-5 mb-4 space-y-1">
            <li>"Zahoot" – Refers to our business directory and AI-powered search platform.</li>
            <li>"User" – Any individual or entity using our platform.</li>
            <li>"Business Listing" – Profiles of businesses featured on Zahoot.</li>
            <li>"We," "Us," "Our" – Refers to Zahoot.</li>
          </ul>
          
          <h2 className="text-lg font-semibold mt-6 mb-3">3. Platform Overview</h2>
          <p className="mb-4">Zahoot provides an AI-powered business directory, allowing users to discover and connect with service providers. We aggregate publicly available reviews and data to help users make informed decisions.</p>
          <p className="font-medium mb-2">How Zahoot Works:</p>
          <ul className="list-disc pl-5 mb-4 space-y-1">
            <li>Businesses are listed regardless of their rating.</li>
            <li>Our "Find an Expert" service only recommends businesses with 4-star ratings or higher (based on trusted sources).</li>
            <li>We integrate third-party review data from platforms like Google, Trustpilot, and Bing.</li>
          </ul>
          
          <h2 className="text-lg font-semibold mt-6 mb-3">4. User Responsibilities</h2>
          <p className="mb-2">By using Zahoot, you agree that you will not:</p>
          <ul className="list-disc pl-5 mb-4 space-y-1">
            <li>Provide false or misleading information.</li>
            <li>Use the platform for any fraudulent, illegal, or harmful activities.</li>
            <li>Scrape, copy, or extract data without permission.</li>
            <li>Submit fake reviews or attempt to manipulate ratings.</li>
            <li>Violate any applicable laws or third-party rights.</li>
          </ul>
          <p className="mb-4">Failure to follow these guidelines may result in suspension or termination of your access.</p>
          
          <h2 className="text-lg font-semibold mt-6 mb-3">5. Business Listings & Reviews</h2>
          <ul className="list-disc pl-5 mb-4 space-y-1">
            <li>Business profiles are created based on publicly available data.</li>
            <li>Businesses can claim and update their profiles, subject to verification.</li>
            <li>Ratings and reviews are aggregated from third-party sources. Zahoot does not edit or control review content.</li>
          </ul>
          <p className="font-medium mb-2">Review Disclaimer:</p>
          <p className="mb-4">We do not verify, modify, or endorse third-party reviews. Reviews are for informational purposes only, and users should conduct their own due diligence before engaging with a business.</p>
          
          <h2 className="text-lg font-semibold mt-6 mb-3">6. AI-Powered Search & Recommendations</h2>
          <ul className="list-disc pl-5 mb-4 space-y-1">
            <li>Zahoot's AI suggests businesses based on relevance, rating, and other criteria.</li>
            <li>AI results are generated without bias and based on publicly available data.</li>
            <li>We do not guarantee the accuracy, completeness, or reliability of AI-driven recommendations.</li>
          </ul>
          
          <h2 className="text-lg font-semibold mt-6 mb-3">7. Business Accounts & Advertising</h2>
          <ul className="list-disc pl-5 mb-4 space-y-1">
            <li>Businesses can claim their listing and provide additional details.</li>
            <li>Paid advertising or promoted listings may be available.</li>
            <li>All business accounts must adhere to our quality standards.</li>
          </ul>
          
          <h2 className="text-lg font-semibold mt-6 mb-3">8. Intellectual Property</h2>
          <ul className="list-disc pl-5 mb-4 space-y-1">
            <li>Zahoot owns all platform content, excluding third-party data.</li>
            <li>Users may not copy, distribute, or modify any content without permission.</li>
            <li>Businesses retain ownership of their branding, logos, and proprietary materials.</li>
          </ul>
          
          <h2 className="text-lg font-semibold mt-6 mb-3">9. Limitation of Liability</h2>
          <p className="mb-2">Zahoot does not:</p>
          <ul className="list-disc pl-5 mb-4 space-y-1">
            <li>Guarantee the accuracy or reliability of business information or reviews.</li>
            <li>Take responsibility for any disputes between users and businesses.</li>
            <li>Provide warranties for services obtained through our platform.</li>
          </ul>
          <p className="mb-4">We are not liable for any losses, damages, or issues arising from platform use.</p>
          
          <h2 className="text-lg font-semibold mt-6 mb-3">10. Privacy & Data Use</h2>
          <ul className="list-disc pl-5 mb-4 space-y-1">
            <li>We collect and process data as outlined in our Privacy Policy.</li>
            <li>Third-party review data is retrieved under fair-use policies.</li>
            <li>Users agree that their interactions on the platform may be used to improve AI recommendations.</li>
          </ul>
          <p className="mb-4">For full details, please review our Privacy Policy.</p>
          
          <h2 className="text-lg font-semibold mt-6 mb-3">11. Third-Party Links & Services</h2>
          <p className="mb-4">Zahoot may contain links to third-party websites. We are not responsible for external content, policies, or practices.</p>
          
          <h2 className="text-lg font-semibold mt-6 mb-3">12. Modifications to These Terms</h2>
          <ul className="list-disc pl-5 mb-4 space-y-1">
            <li>We reserve the right to update these terms at any time.</li>
            <li>Continued use of Zahoot after changes constitutes acceptance.</li>
          </ul>
          
          <h2 className="text-lg font-semibold mt-6 mb-3">13. Termination & Suspension</h2>
          <ul className="list-disc pl-5 mb-4 space-y-1">
            <li>We may suspend or terminate access for violations of these terms.</li>
            <li>Businesses with fraudulent or misleading profiles may be removed without notice.</li>
          </ul>
          
          <h2 className="text-lg font-semibold mt-6 mb-3">14. Governing Law</h2>
          <p className="mb-4">These Terms of Use are governed by the laws of England and Wales.</p>
          
          <h2 className="text-lg font-semibold mt-6 mb-3">15. Contact Us</h2>
          <p className="mb-1">If you have any questions about these terms, please contact us at:</p>
          <p className="mb-1">📍 Address: Louisa Ryland House, 44 Newhall Street, B3 3PL</p>
          <p className="mb-1">📧 Email: support@zahoot.ai</p>
          <p className="mb-4">🌍 Website: zahoot.ai</p>
        </>
      )
    });
    setModalOpen(true);
  };

  const showPrivacyPolicy = () => {
    setModalContent({
      title: "Privacy Policy",
      content: (
        <>
          <h1 className="text-xl font-bold mb-4">Privacy Policy</h1>
          <p className="mb-2 text-gray-400">Effective Date: 6th February 2025</p>
          <p className="mb-3 text-gray-400">Last Updated: 6th February 2025</p>
          <p className="mb-3 text-gray-400">Company: Zahoot</p>
          <p className="mb-3 text-gray-400">Website: zahoot.ai</p>
          <p className="mb-6 text-gray-400">Business Address: Louisa Ryland House, 44 Newhall Street, B3 3PL</p>
          
          <h2 className="text-lg font-semibold mt-6 mb-3">1. Introduction</h2>
          <p className="mb-4">Welcome to Zahoot! Your privacy is important to us. This Privacy Policy explains how we collect, use, store, and protect your data when you use our website (zahoot.ai) and related services.</p>
          <p className="mb-4">By using our platform, you agree to the collection and use of information as described in this policy. If you do not agree, please do not use Zahoot.</p>
          
          <h2 className="text-lg font-semibold mt-6 mb-3">2. Data We Collect</h2>
          <p className="mb-2">We collect and process the following types of personal data:</p>
          
          <h3 className="text-base font-medium mt-4 mb-2">A. Information You Provide</h3>
          <ul className="list-disc pl-5 mb-4 space-y-1">
            <li>Account Information: When you register or claim a business listing, we collect your name, email, business details, and contact information.</li>
            <li>Communication Data: Emails, inquiries, or feedback submitted via contact forms.</li>
          </ul>
          
          <h3 className="text-base font-medium mt-4 mb-2">B. Information Collected Automatically</h3>
          <p className="mb-2">When you visit Zahoot, we automatically collect certain data:</p>
          <ul className="list-disc pl-5 mb-4 space-y-1">
            <li>Device & Usage Data: IP address, browser type, operating system, and interactions with the website.</li>
            <li>Cookies & Tracking: We use cookies to improve your experience, personalise content, and analyse site traffic. See our Cookie Policy for details.</li>
          </ul>
          
          <h3 className="text-base font-medium mt-4 mb-2">C. Third-Party Data</h3>
          <ul className="list-disc pl-5 mb-4 space-y-1">
            <li>Aggregated Business Reviews: We retrieve business reviews from trusted third-party sources like Google, Trustpilot, and Bing.</li>
            <li>Analytics Data: We use tools like Google Analytics and Meta Pixel to understand website usage.</li>
          </ul>
          
          <h2 className="text-lg font-semibold mt-6 mb-3">3. How We Use Your Data</h2>
          <p className="mb-2">We process your data for the following purposes:</p>
          
          <table className="border-collapse w-full mb-4">
            <thead>
              <tr className="bg-purple-900/40">
                <th className="border border-purple-800/30 p-2 text-left">Purpose</th>
                <th className="border border-purple-800/30 p-2 text-left">Legal Basis</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td className="border border-purple-800/30 p-2">To provide and operate Zahoot</td>
                <td className="border border-purple-800/30 p-2">Contractual necessity</td>
              </tr>
              <tr>
                <td className="border border-purple-800/30 p-2">To improve AI-powered search results</td>
                <td className="border border-purple-800/30 p-2">Legitimate interest</td>
              </tr>
              <tr>
                                                  <td className="border border-purple-800/30 p-2">To personalise user experience</td>
                <td className="border border-purple-800/30 p-2">Legitimate interest</td>
              </tr>
              <tr>
                <td className="border border-purple-800/30 p-2">To communicate with users & businesses</td>
                <td className="border border-purple-800/30 p-2">Contractual necessity</td>
              </tr>
              <tr>
                <td className="border border-purple-800/30 p-2">To prevent fraud and enforce terms</td>
                <td className="border border-purple-800/30 p-2">Legal obligation</td>
              </tr>
              <tr>
                <td className="border border-purple-800/30 p-2">To analyse traffic & user engagement</td>
                <td className="border border-purple-800/30 p-2">Legitimate interest</td>
              </tr>
            </tbody>
          </table>
          
          <h2 className="text-lg font-semibold mt-6 mb-3">4. Cookies & Tracking</h2>
          <ul className="list-disc pl-5 mb-4 space-y-1">
            <li>Essential Cookies: Required for site functionality.</li>
            <li>Analytics Cookies: Help us improve Zahoot's performance.</li>
            <li>Marketing Cookies: Used for retargeting ads.</li>
          </ul>
          <p className="mb-4">🔹 You can opt out of non-essential cookies via our Cookie Preferences panel.</p>
          
          <h2 className="text-lg font-semibold mt-6 mb-3">5. Data Sharing & Third Parties</h2>
          <p className="mb-2">We do not sell your personal data. However, we may share it with:</p>
          <ol className="list-decimal pl-5 mb-4 space-y-1">
            <li>Service Providers: Hosting providers, analytics tools, and payment processors.</li>
            <li>Legal Compliance: If required by law or to prevent fraud.</li>
            <li>Aggregated Review Data: Third-party sources (e.g., Google, Trustpilot) provide business reviews that we display.</li>
          </ol>
          
          <h3 className="text-base font-medium mt-4 mb-2">🌍 International Transfers</h3>
          <p className="mb-4">Some services we use may store data outside the UK/EU. We ensure all transfers comply with GDPR safeguards.</p>
          
          <h2 className="text-lg font-semibold mt-6 mb-3">6. Your Rights (GDPR & UK Data Protection Act 2018)</h2>
          <p className="mb-2">You have the right to:</p>
          <ul className="list-disc pl-5 mb-4 space-y-1">
            <li>Access your data</li>
            <li>Request correction of inaccurate data</li>
            <li>Request deletion of your data ("Right to be Forgotten")</li>
            <li>Restrict or object to processing</li>
            <li>Request data portability</li>
            <li>Withdraw consent for marketing communications</li>
          </ul>
          <p className="mb-4">🔹 To exercise your rights, contact us at compliance@zahoot.ai</p>
          
          <h2 className="text-lg font-semibold mt-6 mb-3">7. Data Retention</h2>
          <ul className="list-disc pl-5 mb-4 space-y-1">
            <li>We store personal data only as long as necessary for legal, business, or analytical purposes.</li>
            <li>Reviews and business details retrieved from third-party sources are updated automatically and not permanently stored by us.</li>
          </ul>
          
          <h2 className="text-lg font-semibold mt-6 mb-3">8. Security Measures</h2>
          <p className="mb-2">We take appropriate measures to protect your data, including:</p>
          <ul className="list-disc pl-5 mb-4 space-y-1">
            <li>🔹 Encryption: Secure HTTPS protocol for data transmission.</li>
            <li>🔹 Access Controls: Restricted access to personal data.</li>
            <li>🔹 Data Anonymization: When possible, we anonymise stored data.</li>
          </ul>
          
          <h2 className="text-lg font-semibold mt-6 mb-3">9. Third-Party Links</h2>
          <p className="mb-4">Zahoot may contain links to third-party websites (e.g., Google Reviews, Trustpilot). Clicking these links means you leave Zahoot—we are not responsible for their privacy practices.</p>
          
          <h2 className="text-lg font-semibold mt-6 mb-3">10. Changes to This Policy</h2>
          <p className="mb-4">We may update this Privacy Policy from time to time. The latest version will always be available on our website. Continued use of Zahoot after updates constitutes acceptance.</p>
          
          <h2 className="text-lg font-semibold mt-6 mb-3">11. Contact Us</h2>
          <p className="mb-1">📍 Address: Louisa Ryland House, 44 Newhall Street, B3 3PL</p>
          <p className="mb-1">📧 Email: compliance@zahoot.ai</p>
          <p className="mb-4">🌍 Website: zahoot.ai</p>
        </>
      )
    });
    setModalOpen(true);
  };

  // Verification Code Form
  if (formStep === 2) {
    return (
      <form onSubmit={handleVerify} className="space-y-5">
        {errors.verificationCode && (
          <div className="p-3 bg-red-500/10 border border-red-500/20 text-red-400 text-sm rounded-lg">
            {errors.verificationCode}
          </div>
        )}
        
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-300">Verification Code</label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <FaShieldAlt className="h-4 w-4 text-gray-400" />
            </div>
            <input
              type="text"
              name="verificationCode"
              placeholder="6-digit code"
              maxLength={6}
              className="w-full pl-10 pr-4 py-3 bg-white/5 border border-white/10 focus:border-purple-500/50 text-gray-100 rounded-lg focus:ring-2 focus:ring-purple-500/20 transition-all"
              value={formData.verificationCode}
              onChange={handleChange}
            />
          </div>
        </div>
        
        <button
          type="submit"
          className="w-full py-3 px-4 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white font-medium rounded-lg transition-all transform hover:scale-[1.02] active:scale-[0.98] disabled:opacity-70 disabled:cursor-not-allowed"
          disabled={isVerifying}
        >
          {isVerifying ? (
            <span className="flex items-center justify-center">
              <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Verifying...
            </span>
          ) : "Verify Email"}
        </button>
        
        <div className="text-center space-y-2">
          <p className="text-sm text-gray-300">
            Didn't receive the code?{' '}
            <button
              type="button"
              onClick={handleResendCode}
              className="text-purple-400 hover:text-purple-300 font-medium transition-colors"
            >
              Resend Code
            </button>
          </p>
          
          <p className="text-sm text-gray-300">
            <button
              type="button"
              onClick={handleBackToSignup}
              className="text-purple-400 hover:text-purple-300 font-medium transition-colors"
            >
              Back to Sign Up
            </button>
          </p>
        </div>
      </form>
    );
  }

  // Registration Form
  return (
    <>
      <form onSubmit={handleSubmit} className="space-y-5">
        {isFromAiSearch && (
          <div className="p-4 bg-gradient-to-r from-purple-600/20 to-pink-600/20 border border-purple-500/30 rounded-lg">
            <div className="flex items-center mb-2">
              <div className="flex-shrink-0">
                <svg className="h-6 w-6 text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
              <h3 className="ml-2 text-lg font-medium text-white">
                🎉 Get 3 Free AI Searches!
              </h3>
            </div>
            <p className="text-gray-300 text-sm">
              Create your account to unlock powerful AI-powered solicitor search. Find the perfect legal expert with natural language queries like "divorce lawyers in London with great reviews".
            </p>
          </div>
        )}
        
        {errors.form && (
          <div className="p-3 bg-red-500/10 border border-red-500/20 text-red-400 text-sm rounded-lg">
            {errors.form}
          </div>
        )}

        {/* Name field */}
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-300">Full Name</label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <FaUser className="h-4 w-4 text-gray-400" />
            </div>
            <input
              type="text"
              name="name"
              placeholder="John Smith"
              className="w-full pl-10 pr-4 py-3 bg-white/5 border border-white/10 focus:border-purple-500/50 text-gray-100 rounded-lg focus:ring-2 focus:ring-purple-500/20 transition-all"
              value={formData.name}
              onChange={handleChange}
            />
          </div>
          {errors.name && (
            <p className="text-red-400 text-sm mt-1">{errors.name}</p>
          )}
        </div>

        {/* Email field */}
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-300">Email</label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <FaEnvelope className="h-4 w-4 text-gray-400" />
            </div>
            <input
              type="email"
              name="email"
              placeholder="you@example.com"
              className="w-full pl-10 pr-4 py-3 bg-white/5 border border-white/10 focus:border-purple-500/50 text-gray-100 rounded-lg focus:ring-2 focus:ring-purple-500/20 transition-all"
              value={formData.email}
              onChange={handleChange}
            />
          </div>
          {errors.email && (
            <p className="text-red-400 text-sm mt-1">{errors.email}</p>
          )}
        </div>

        {/* Password field */}
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-300">Password</label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <FaLock className="h-4 w-4 text-gray-400" />
            </div>
            <input
              type="password"
              name="password"
              placeholder="••••••••"
              className="w-full pl-10 pr-4 py-3 bg-white/5 border border-white/10 focus:border-purple-500/50 text-gray-100 rounded-lg focus:ring-2 focus:ring-purple-500/20 transition-all"
              value={formData.password}
              onChange={handleChange}
            />
          </div>
          {errors.password && (
            <p className="text-red-400 text-sm mt-1">{errors.password}</p>
          )}
        </div>

        {/* Confirm Password field */}
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-300">Confirm Password</label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <FaLock className="h-4 w-4 text-gray-400" />
            </div>
            <input
              type="password"
              name="confirmPassword"
              placeholder="••••••••"
              className="w-full pl-10 pr-4 py-3 bg-white/5 border border-white/10 focus:border-purple-500/50 text-gray-100 rounded-lg focus:ring-2 focus:ring-purple-500/20 transition-all"
              value={formData.confirmPassword}
              onChange={handleChange}
            />
          </div>
          {errors.confirmPassword && (
            <p className="text-red-400 text-sm mt-1">{errors.confirmPassword}</p>
          )}
        </div>

        {/* Terms and Privacy Policy checkbox */}
        <div className="space-y-2">
          <div className="flex items-start">
            <div className="flex items-center h-5">
              <input
                id="terms"
                name="acceptTerms"
                type="checkbox"
                checked={formData.acceptTerms}
                onChange={handleChange}
                className="w-4 h-4 bg-white/5 border border-white/10 rounded focus:ring-2 focus:ring-purple-500/20 text-purple-600"
              />
            </div>
            <div className="ml-3 text-sm">
              <label htmlFor="terms" className="text-gray-300">
                I agree to the{" "}
                <button
                  type="button"
                  className="text-purple-400 hover:text-purple-300 underline"
                  onClick={showTerms}
                >
                  Terms of Use
                </button>{" "}
                and{" "}
                <button
                  type="button"
                  className="text-purple-400 hover:text-purple-300 underline"
                  onClick={showPrivacyPolicy}
                >
                  Privacy Policy
                </button>
              </label>
            </div>
          </div>
          {errors.acceptTerms && (
            <p className="text-red-400 text-sm">{errors.acceptTerms}</p>
          )}
        </div>

        {/* Submit button */}
        <button
          type="submit"
          disabled={isLoading}
          className="w-full py-3 px-4 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white font-medium rounded-lg transition-all transform hover:scale-[1.02] active:scale-[0.98] disabled:opacity-70 disabled:cursor-not-allowed"
        >
          {isLoading ? (
            <span className="flex items-center justify-center">
              <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Creating Account...
            </span>
          ) : "Create Account"}
        </button>

        {/* Divider */}
        <div className="relative">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-white/10"></div>
          </div>
          <div className="relative flex justify-center text-sm">
            <span className="px-2 bg-[#301555] text-gray-400">Or continue with</span>
          </div>
        </div>

        {/* Google signup */}
        <button
          type="button"
          onClick={handleGoogleSignIn}
          className="w-full flex items-center justify-center gap-2 py-3 px-4 bg-white/5 hover:bg-white/10 border border-white/10 text-gray-300 font-medium rounded-lg transition-all"
        >
          <FaGoogle className="h-5 w-5" />
          Google
        </button>
      </form>
      
      {/* Terms Modal */}
      <TermsModal 
        isOpen={modalOpen} 
        onClose={() => setModalOpen(false)} 
        title={modalContent.title}
        content={modalContent.content}
      />
    </>
  );
}; 